import { computed, makeObservable, observable } from 'mobx';
import { rootStore } from 'src/app';
import client from 'src/feathers';
import { sortAlph, toNumberOrUndefined } from 'src/util/util';
import { DataType, ISpace, ISpaceUser, ProjectTheme, SpaceType, UserRole } from '../types';
import { Data, User } from './';



export class Space extends Data<ISpace> implements ISpace {
  title:         string       = null;
  parent?:       number       = null;
  project_id?:   number       = null;
  type:          SpaceType    = null;
  agenda_id?:    number       = null;
  theme?:        ProjectTheme = null;
  start_date?:   Date         = null;
  end_date?:     Date         = null;
  active_users?: User[]       = [];
  active_meeting_id?: number  = null;
  member_listing?:    "name" | "title" = null;

  constructor(data: ISpace) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Space, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      active_meeting_id: observable,
      active_users:      observable,
      agenda_id:         observable,
      end_date:          observable,
      member_listing:    observable,
      start_date:        observable,
      theme:             observable,
      title:             observable,

      isSubRoom:      computed,
      getRoomSubTree: computed,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  public static create(data?: ISpace): Space {
    return new Space(data || {
      title: "",
      type:  SpaceType.Room,
      workspace_id: rootStore.authStore.getCurWorkspaceID,
      project_id:   rootStore.authStore.getCurProjectID,
    });
  }

  protected fromData(data: ISpace): void {
    super.fromData(data);
    this.active_meeting_id = toNumberOrUndefined(data.active_meeting_id);
    this.agenda_id         = toNumberOrUndefined(data.agenda_id);
    this.end_date          = data.end_date   && new Date(data.end_date);
    this.member_listing    = data.member_listing;
    this.parent            = toNumberOrUndefined(data.parent);
    this.start_date        = data.start_date && new Date(data.start_date);
    this.theme             = data.theme;
    this.title             = data.title;
    this.type              = data.type;
    this.project_id        = toNumberOrUndefined(data.project_id);
  }

  public toData(): any {
    return {
        ...super.toData(),
        agenda_id:         this.agenda_id || undefined,
        end_date:          this.end_date   && this.end_date.toISOString(),
        member_listing:    this.member_listing,
        parent:            this.parent || undefined,
        start_date:        this.start_date && this.start_date.toISOString(),
        theme:             this.theme,
        title:             this.title,
        type:              this.type,
        active_meeting_id: this.active_meeting_id || undefined,
        project_id:        this.project_id,
    };
  }

  protected toPatchData(patch: Partial<ISpace>): any {
    return {
        ...super.toPatchData(patch),
        title:             "title" in patch ? patch.title : undefined,
        parent:            "parent" in patch ? patch.parent : undefined,
        type:              "type" in patch ? patch.type : undefined,
        agenda_id:         "agenda_id" in patch ? patch.agenda_id : undefined,
        theme:             "theme" in patch ? patch.theme : undefined,
        start_date:        "start_date" in patch ? patch.start_date && patch.start_date.toISOString() : undefined,
        end_date:          "end_date" in patch ? patch.end_date   && patch.end_date.toISOString() : undefined,
        member_listing:    "member_listing" in patch ? patch.member_listing : undefined,
        active_meeting_id: "active_meeting_id" in patch ? patch.active_meeting_id : undefined,
        project_id:        "project_id" in patch ? patch.project_id : undefined,
    };
  }

  public linkUser = async (user_id: number, role: UserRole): Promise<void> => {
    const space_user: ISpaceUser = {
      user_id:      user_id,
      space_id:     this.id,
      space_type:   this.type,
      workspace_id: this.workspace_id,
      role:         role
    }
    return client.service("space-users").create(space_user);
  }

  get isSubRoom(): boolean {
    return this.type === SpaceType.Room && this.project_id !== this.parent;
  }

  /**
   * @return the depth of the room in the room subtree
   */
  get getRoomDepth(): number {
    return this.getRoomSubTree.length-1;
  }

  
  /**
   * @return array of spaces that contain @this 
   * and all its parents up to the workspace in order, e.g.
   * [workspace, project, room, subroom1, space_id room]
   */
  get getRoomSubTree(): Space[] {
    if(
      !this.isSubRoom || // If this is not a subroom we are done
      !rootStore.spaceStore.container[this.parent] || // next check if the user has access to the parent
      !rootStore.authStore.getCurUser.spaces[this.parent]
    ) return [this];
    if(!rootStore.spaceStore.container[this.parent]) return [this]; // Extra check if not all spaces loaded yet
    return [...rootStore.spaceStore.container[this.parent].getRoomSubTree, this];
  }
}

